/**
 * Format a timestamp as "yyyy-mm-dd hh:mm:ss" in local time.
 *
 * - Date-only ISO strings ("yyyy-mm-dd", e.g. all-day calendar events) are
 *   returned unchanged, since they carry no time component.
 * - null / undefined / empty → "—".
 * - Unparseable values are returned as-is.
 */
export function formatTimestamp(value) {
    if (value === null || value === undefined || value === "")
        return "—";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value))
        return value;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime()))
        return String(value);
    const p = (n) => String(n).padStart(2, "0");
    const date = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    const time = `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    return `${date} ${time}`;
}
/**
 * Format an ISO timestamp as a human-readable "X ago" string.
 * - < 60s  → "acum Xs"
 * - < 3600s → "acum Xm Ys"
 * - else   → "acum Xh Ym"
 * Returns "—" for null/unparseable values.
 */
export function formatTimeAgo(value, now) {
    if (!value)
        return "—";
    const ts = new Date(value).getTime();
    if (Number.isNaN(ts))
        return "—";
    const diffSec = Math.floor(((now ?? Date.now()) - ts) / 1000);
    if (diffSec < 0)
        return "acum";
    if (diffSec < 60)
        return `acum ${diffSec}s`;
    const m = Math.floor(diffSec / 60);
    if (m < 60) {
        const s = diffSec % 60;
        return s > 0 ? `acum ${m}m ${s}s` : `acum ${m}m`;
    }
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return rm > 0 ? `acum ${h}h ${rm}m` : `acum ${h}h`;
}
/**
 * Return the number of whole days until the given ISO date/datetime.
 * Negative = already expired. Returns null for null/unparseable values.
 */
export function daysUntil(value, now) {
    if (!value)
        return null;
    const d = new Date(value);
    if (isNaN(d.getTime()))
        return null;
    return Math.floor((d.getTime() - (now ?? Date.now())) / (1000 * 60 * 60 * 24));
}
